import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/atoms/radio-group';
import { Button } from '@/components/atoms/button';

/**
 * Minimal shape the dialog needs to render a variant row. The picker passes its
 * already-normalized line items, which satisfy this contract.
 */
export interface ConfigurableVariant {
  id: string;
  name: string;
  code: string;
  basePrice?: number | string | null;
  costBasis?: number;
  markupPercent?: number;
  unit?: string | null;
}

interface ConfigurableVariantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentName: string;
  variants: ConfigurableVariant[];
  onSelect: (variant: ConfigurableVariant) => void;
}

function toNumber(value: number | string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

/**
 * Configurable EQUIPMENT prompt: when an operator adds a configurable placeholder
 * item to an estimate/job/invoice, they must pick exactly one configuration of
 * the linked type. That configuration — not the placeholder — becomes the line.
 */
export function ConfigurableVariantDialog({
  open,
  onOpenChange,
  parentName,
  variants,
  onSelect,
}: ConfigurableVariantDialogProps) {
  const [selectedId, setSelectedId] = useState<string>('');

  // Default the selection to the first variant whenever the set changes (e.g.
  // the dialog reopens for a different configurable item).
  useEffect(() => {
    if (!open) return;
    setSelectedId(variants[0]?.id ?? '');
  }, [open, variants]);

  const selectedVariant = useMemo(
    () => variants.find((variant) => variant.id === selectedId),
    [variants, selectedId],
  );

  const handleConfirm = () => {
    if (!selectedVariant) return;
    onSelect(selectedVariant);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Choose a configuration</DialogTitle>
          <DialogDescription>
            Select which configuration of “{parentName}” to add.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={selectedId}
          onValueChange={setSelectedId}
          className="max-h-72 gap-2 overflow-auto"
        >
          {variants.map((variant) => {
            const price = toNumber(variant.basePrice);
            const cost = variant.costBasis;
            const markup = variant.markupPercent;
            const meta: string[] = [];
            if (variant.code) meta.push(variant.code);
            if (typeof cost === 'number' && cost > 0) meta.push(`cost ${formatCurrency(cost)}`);
            if (typeof markup === 'number' && markup > 0) meta.push(`${markup}% markup`);
            return (
              <label
                key={variant.id}
                htmlFor={`variant-${variant.id}`}
                className="flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors hover:bg-accent has-[:checked]:border-primary has-[:checked]:bg-accent/50"
              >
                <RadioGroupItem id={`variant-${variant.id}`} value={variant.id} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{variant.name}</p>
                  {meta.length > 0 ? (
                    <p className="truncate text-xs text-muted-foreground">{meta.join(' · ')}</p>
                  ) : null}
                </div>
                <span className="shrink-0 text-sm font-medium tabular-nums">
                  {formatCurrency(price)}
                </span>
              </label>
            );
          })}
        </RadioGroup>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!selectedVariant}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
